import type { ApiResponse } from "@dm-online/shared";
import { DEFAULT_FORMAT, getCardById, isDeckFormat } from "@dm-online/shared";

// ゲストのデッキはサーバーに置かず、このブラウザの localStorage に保存する。
// api.ts から /decks 系のリクエストをここへ振り分ける（レスポンス形は worker の decks ルートに揃える）。

const STORAGE_KEY = "dm-guest-decks";
const MEMO_MAX_LENGTH = 2000;

interface LocalDeck {
  id: string;
  name: string;
  card_ids: string[];
  key_card_id: string | null;
  format: string;
  share_code: null; // 共有コードはサーバー採番なのでゲストは持たない
  memo: string;
  created_at: number;
  updated_at: number;
}

function load(): LocalDeck[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(decks: LocalDeck[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(decks));
    return true;
  } catch {
    return false;
  }
}

function deckContainsCard(cardIds: string[], keyCardId: string): boolean {
  const keyCanon = getCardById(keyCardId)?.id ?? keyCardId;
  return cardIds.some((id) => (getCardById(id)?.id ?? id) === keyCanon);
}

const ok = <T>(data: T): ApiResponse<T> => ({ ok: true, data });
const fail = <T>(error: string): ApiResponse<T> => ({ ok: false, error });

type Body = {
  name?: string;
  cardIds?: string[];
  keyCardId?: string | null;
  format?: string;
  memo?: string;
};

// ゲストのローカル保存で処理する対象か（デッキコード取得・画像読み取りはサーバー）
export function isLocalDeckPath(path: string): boolean {
  if (path.startsWith("/decks/code/") || path.startsWith("/decks/scan-image")) return false;
  return path === "/decks" || path.startsWith("/decks/");
}

export function handleLocalDecks<T>(path: string, method: string, body: Body): ApiResponse<T> {
  const id = path.split("/")[2];
  const decks = load();
  const now = Date.now();
  const cast = (v: unknown) => v as ApiResponse<T>;

  if (method === "GET" && !id) {
    return cast(ok([...decks].sort((a, b) => b.updated_at - a.updated_at)));
  }

  if (method === "GET") {
    const d = decks.find((x) => x.id === id);
    return d ? cast(ok(d)) : fail("見つかりません");
  }

  if (method === "POST" && !id) {
    if (!body.name || !Array.isArray(body.cardIds)) return fail("name と cardIds が必要です");
    const deck: LocalDeck = {
      id: crypto.randomUUID(),
      name: body.name,
      card_ids: body.cardIds,
      key_card_id: body.keyCardId && deckContainsCard(body.cardIds, body.keyCardId) ? body.keyCardId : null,
      format: isDeckFormat(body.format) ? body.format : DEFAULT_FORMAT,
      share_code: null,
      memo: typeof body.memo === "string" ? body.memo.slice(0, MEMO_MAX_LENGTH) : "",
      created_at: now,
      updated_at: now,
    };
    if (!save([...decks, deck])) return fail("ブラウザに保存できませんでした");
    return cast(ok(deck));
  }

  if (method === "PUT" && id) {
    const d = decks.find((x) => x.id === id);
    if (!d) return fail("見つかりません");
    if (body.name !== undefined) d.name = body.name;
    if (body.cardIds) d.card_ids = body.cardIds;
    if ("keyCardId" in body) {
      d.key_card_id =
        body.keyCardId && deckContainsCard(body.cardIds ?? d.card_ids, body.keyCardId) ? body.keyCardId : null;
    }
    if ("format" in body) d.format = isDeckFormat(body.format) ? body.format : DEFAULT_FORMAT;
    if ("memo" in body) d.memo = typeof body.memo === "string" ? body.memo.slice(0, MEMO_MAX_LENGTH) : "";
    d.updated_at = now;
    if (!save(decks)) return fail("ブラウザに保存できませんでした");
    return cast(ok({ ok: true }));
  }

  if (method === "DELETE" && id) {
    if (!decks.some((x) => x.id === id)) return fail("見つかりません");
    save(decks.filter((x) => x.id !== id));
    return cast(ok({ ok: true }));
  }

  return fail("未対応の操作です");
}
